/**
 * Download Routes Hook
 * 
 * Custom hook for handling route download operations with fetching, import,
 * progress tracking, and error handling.
 * 
 * @module useDownloadRoutes
 */

import { useState, useCallback } from 'react';
import { useFixedRoutes } from '../context/FixedRoutesContext';
import { validateForDownload, type DownloadValidationData } from '../services/routesTransferValidation';
import {
  fetchOperationsSolutions,
  type Environment,
  type UATOperationsSolutionsResponse,
} from '../services/fixedRouteUatClient';
import {
  extractUATStops,
  extractUATRoutes,
  importUATStops,
  importUATRoutes,
  type StopImportResult,
  type RouteImportResult,
} from '../services/fixedRouteUatImporter';
import { handleApiError } from '../utils/errorHandling';
import type { RouteOperationResult } from '../components/map/components/routes-transfer/OperationResults';
import type { UATRoute } from '../services/fixedRouteUatClient';

/**
 * Download progress for a single route
 */
export interface RouteDownloadProgress {
  routeId: string;
  routeName: string;
  status: 'pending' | 'fetching' | 'importing' | 'success' | 'error';
  message: string;
  progress?: number; // 0-100
}

/**
 * Download state
 */
export interface DownloadState {
  /** Whether download is in progress */
  isDownloading: boolean;
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Whether fetching is in progress */
  isFetching: boolean;
  /** Download progress for each route */
  progress: Map<string, RouteDownloadProgress>;
  /** Download results */
  results: RouteOperationResult[];
  /** Validation results */
  validation: DownloadValidationData | null;
  /** Current error */
  error: string | null;
  /** Fetched operations solutions */
  operationsSolutions: UATOperationsSolutionsResponse | null;
}

/**
 * Hook return type
 */
export interface UseDownloadRoutesReturn {
  /** Download state */
  downloadState: DownloadState;
  /** Start download operation */
  startDownload: (
    selectedRouteIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      serviceDate: string;
      timezone: string;
      downloadAcknowledged: boolean;
      environment: Environment;
      selectedRouteNames?: string[]; // For fallback matching when IDs don't match
    }
  ) => Promise<void>;
  /** Validate routes before download */
  validateRoutes: (
    selectedRouteIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      serviceDate?: string;
      timezone?: string;
      downloadAcknowledged?: boolean;
    }
  ) => Promise<DownloadValidationData | null>;
  /** Reset download state */
  resetDownload: () => void;
}

/**
 * useDownloadRoutes Hook
 * 
 * Manages route download operations with fetching, import, and progress tracking.
 */
export function useDownloadRoutes(): UseDownloadRoutesReturn {
  const { routes, stops, addStop, updateStop, addRoute } = useFixedRoutes();
  const [downloadState, setDownloadState] = useState<DownloadState>({
    isDownloading: false,
    isValidating: false,
    isFetching: false,
    progress: new Map(),
    results: [],
    validation: null,
    error: null,
    operationsSolutions: null,
  });

  /**
   * Validate routes before download
   */
  const validateRoutes = useCallback(
    async (
      selectedRouteIds: Set<string>,
      prerequisites: {
        jwtToken: string;
        productId: string;
        networkId: number | null;
        serviceDate?: string;
        timezone?: string;
        downloadAcknowledged?: boolean;
      }
    ): Promise<DownloadValidationData | null> => {
      if (selectedRouteIds.size === 0) {
        return null;
      }

      setDownloadState((prev) => ({ ...prev, isValidating: true, error: null }));

      try {
        // Convert selected route IDs to UATRoute format (simplified)
        // In a real scenario, we'd need to fetch the actual route data
        const selectedRoutes: UATRoute[] = []; // TODO: Fetch actual route data

        // Validate
        const validation = await validateForDownload(selectedRoutes, {
          jwtToken: prerequisites.jwtToken,
          productId: prerequisites.productId,
          networkId: prerequisites.networkId,
          serviceDate: prerequisites.serviceDate,
          timezone: prerequisites.timezone,
          downloadAcknowledged: prerequisites.downloadAcknowledged,
          selectedRouteIds,
        });

        setDownloadState((prev) => ({
          ...prev,
          validation,
          isValidating: false,
        }));

        return validation;
      } catch (error) {
        const friendlyError = handleApiError(error, 'Route validation');
        setDownloadState((prev) => ({
          ...prev,
          isValidating: false,
          error: friendlyError.message,
        }));
        return null;
      }
    },
    []
  );

  /**
   * Start download operation
   */
  const startDownload = useCallback(
    async (
      selectedRouteIds: Set<string>,
      prerequisites: {
        jwtToken: string;
        productId: string;
        networkId: number | null;
        serviceDate: string;
        timezone: string;
        downloadAcknowledged: boolean;
        environment: Environment;
        selectedRouteNames?: string[]; // For fallback matching when IDs don't match
      }
    ) => {
      if (selectedRouteIds.size === 0) {
        return;
      }

      // Initialize progress
      const progressMap = new Map<string, RouteDownloadProgress>();
      selectedRouteIds.forEach((routeId) => {
        progressMap.set(routeId, {
          routeId,
          routeName: `Route ${routeId}`, // Will be updated when we fetch data
          status: 'pending',
          message: 'Waiting...',
          progress: 0,
        });
      });

      setDownloadState({
        isDownloading: true,
        isValidating: false,
        isFetching: true,
        progress: progressMap,
        results: [],
        validation: null,
        error: null,
        operationsSolutions: null,
      });

      const results: RouteOperationResult[] = [];

      try {
        // Step 1: Fetch operations solutions
        const allProgress = Array.from(progressMap.values());
        allProgress.forEach((p) => {
          p.status = 'fetching';
          p.message = 'Fetching route data...';
          p.progress = 10;
        });
        setDownloadState((prev) => ({
          ...prev,
          progress: new Map(progressMap),
        }));

        const operationsSolutions = await fetchOperationsSolutions(
          prerequisites.jwtToken,
          prerequisites.productId,
          prerequisites.serviceDate,
          prerequisites.timezone,
          prerequisites.environment
        );

        setDownloadState((prev) => ({
          ...prev,
          operationsSolutions,
          isFetching: false,
        }));

        // Step 2: Extract stops and routes
        allProgress.forEach((p) => {
          p.status = 'importing';
          p.message = 'Extracting stops and routes...';
          p.progress = 30;
        });
        setDownloadState((prev) => ({
          ...prev,
          progress: new Map(progressMap),
        }));

        const uatStops = extractUATStops(operationsSolutions);
        const uatRoutes = extractUATRoutes(operationsSolutions, prerequisites.timezone);

        // Log only if debugging is needed
        // console.log('[useDownloadRoutes] Extracted routes:', {
        //   totalRoutes: uatRoutes.length,
        //   selectedRouteIds: Array.from(selectedRouteIds),
        //   allExtractedRouteIds: uatRoutes.map((r) => r.routeId),
        //   extractedRouteNames: uatRoutes.map((r) => r.routeName),
        // });

        // Filter to selected routes only
        // First try matching by ID
        let selectedUatRoutes = uatRoutes.filter((r) => selectedRouteIds.has(r.routeId));

        // If no routes matched by ID, try matching by name as fallback
        // This happens because fetchAllRoutes uses getroutelist which returns different IDs
        // than extractUATRoutes (which uses getoperationssolutionsall)
        if (selectedUatRoutes.length === 0 && selectedRouteIds.size > 0 && prerequisites.selectedRouteNames) {
          console.warn('[useDownloadRoutes] No routes matched by ID. Trying to match by name...');
          console.warn('[useDownloadRoutes] Selected route names:', prerequisites.selectedRouteNames);
          
          // Normalize route names for matching (trim whitespace, remove version suffixes)
          const normalizeName = (name: string): string => {
            return name.trim().replace(/\s+Version\s+\d+$/i, '').trim();
          };
          
          selectedUatRoutes = uatRoutes.filter((r) => {
            const normalizedRouteName = normalizeName(r.routeName);
            const normalizedRouteLabel = normalizeName(r.route.label || '');
            return prerequisites.selectedRouteNames!.some((name: string) => {
              const normalizedSelectedName = normalizeName(name);
              // Try multiple matching strategies:
              // 1. Exact match (original names)
              // 2. Exact match (normalized names)
              // 3. Partial match (normalized names)
              // 4. Case-insensitive match
              return (
                name.trim() === r.routeName ||
                name.trim() === r.route.label ||
                normalizedSelectedName === normalizedRouteName ||
                normalizedSelectedName === normalizedRouteLabel ||
                normalizedRouteName.toLowerCase() === normalizedSelectedName.toLowerCase() ||
                normalizedRouteLabel.toLowerCase() === normalizedSelectedName.toLowerCase() ||
                normalizedRouteName.startsWith(normalizedSelectedName) ||
                normalizedSelectedName.startsWith(normalizedRouteName) ||
                normalizedRouteLabel.startsWith(normalizedSelectedName) ||
                normalizedSelectedName.startsWith(normalizedRouteLabel)
              );
            });
          });
          
          if (selectedUatRoutes.length === 0) {
            console.warn('[useDownloadRoutes] No routes matched by name. Selected:', prerequisites.selectedRouteNames, 'Available:', uatRoutes.map((r) => r.routeName));
          }
        }

        // Log only if debugging is needed
        // console.log('[useDownloadRoutes] Filtered selected routes:', {
        //   selectedCount: selectedUatRoutes.length,
        //   selectedRouteIds: selectedUatRoutes.map((r) => r.routeId),
        // });

        // Step 3: Import stops
        allProgress.forEach((p) => {
          p.message = 'Importing stops...';
          p.progress = 50;
        });
        setDownloadState((prev) => ({
          ...prev,
          progress: new Map(progressMap),
        }));

        const stopResults = importUATStops(uatStops, stops);

        // Apply stop imports and build updated stops map
        let createdStops = 0;
        let updatedStopsCount = 0;
        let skippedStops = 0;

        // Build map of all stops (existing + newly created/updated)
        const updatedStopsMap = new Map<string, any>();
        stops.forEach((stop) => {
          updatedStopsMap.set(stop.id, stop);
        });

        for (const result of stopResults) {
          if (result.action === 'created') {
            addStop(result.stop);
            updatedStopsMap.set(result.stop.id, result.stop);
            createdStops++;
          } else if (result.action === 'updated') {
            updateStop(result.stop);
            updatedStopsMap.set(result.stop.id, result.stop);
            updatedStopsCount++;
          } else {
            skippedStops++;
          }
        }

        // Convert map to array for route import
        const updatedStops = Array.from(updatedStopsMap.values());

        // Step 4: Import routes (all at once for better stop matching)
        allProgress.forEach((p) => {
          p.message = 'Importing routes...';
          p.progress = 70;
        });
        setDownloadState((prev) => ({
          ...prev,
          progress: new Map(progressMap),
        }));

        try {
          // Log only if debugging is needed
          // console.log('[useDownloadRoutes] Importing routes:', {
          //   selectedRoutesCount: selectedUatRoutes.length,
          //   updatedStopsCount: updatedStops.length,
          //   existingRoutesCount: routes.length,
          //   uatStopsCount: uatStops.size || 0,
          // });

          // Import all routes at once
          const routeResults = importUATRoutes(
            selectedUatRoutes,
            updatedStops,
            routes,
            uatStops,
            prerequisites.timezone
          );

          // Log only if debugging is needed
          // console.log('[useDownloadRoutes] Route import results:', {
          //   totalResults: routeResults.length,
          //   created: routeResults.filter((r) => r.action === 'created').length,
          //   skipped: routeResults.filter((r) => r.action === 'skipped').length,
          //   skippedReasons: routeResults
          //     .filter((r) => r.action === 'skipped')
          //     .map((r) => r.reason)
          //     .slice(0, 5), // First 5 reasons
          // });

          // Step 5: Apply route imports and update progress
          const totalRoutes = selectedUatRoutes.length;
          for (let i = 0; i < routeResults.length; i++) {
            const routeResult = routeResults[i];
            const uatRoute = selectedUatRoutes[i];
            const progress = progressMap.get(uatRoute.routeId);

            if (progress) {
              progress.message = `Processing route ${i + 1} of ${totalRoutes}...`;
              progress.progress = 70 + (i / totalRoutes) * 30;
              setDownloadState((prev) => ({
                ...prev,
                progress: new Map(progressMap),
              }));
            }

            if (routeResult.action === 'created') {
              // Log only if debugging is needed
              // console.log('[useDownloadRoutes] Adding route:', {
              //   routeId: routeResult.route.id,
              //   routeName: routeResult.route.name,
              //   stopsCount: routeResult.route.stops.length,
              // });
              addRoute(routeResult.route);
              if (progress) {
                progress.status = 'success';
                progress.message = 'Imported successfully';
                progress.progress = 100;
              }
              results.push({
                routeId: uatRoute.routeId,
                routeName: uatRoute.routeName,
                success: true,
                info: `Created route with ${routeResult.route.stops.length} stops`,
              });
            } else {
              if (progress) {
                progress.status = 'error';
                progress.message = routeResult.reason || 'Skipped';
                progress.progress = 100;
              }
              results.push({
                routeId: uatRoute.routeId,
                routeName: uatRoute.routeName,
                success: false,
                error: routeResult.reason || 'Route was skipped',
              });
            }
          }
        } catch (error) {
          const friendlyError = handleApiError(error, 'Import routes');
          // Mark all routes as failed
          selectedUatRoutes.forEach((uatRoute) => {
            const progress = progressMap.get(uatRoute.routeId);
            if (progress) {
              progress.status = 'error';
              progress.message = friendlyError.message;
              progress.progress = 100;
            }
            results.push({
              routeId: uatRoute.routeId,
              routeName: uatRoute.routeName,
              success: false,
              error: friendlyError.message,
            });
          });
        }

        // Finalize
        setDownloadState((prev) => ({
          ...prev,
          isDownloading: false,
          results,
        }));
      } catch (error) {
        const friendlyError = handleApiError(error, 'Download routes');
        setDownloadState((prev) => ({
          ...prev,
          isDownloading: false,
          isFetching: false,
          error: friendlyError.message,
        }));
      }
    },
    [routes, stops, addStop, updateStop, addRoute]
  );

  /**
   * Reset download state
   */
  const resetDownload = useCallback(() => {
    setDownloadState({
      isDownloading: false,
      isValidating: false,
      isFetching: false,
      progress: new Map(),
      results: [],
      validation: null,
      error: null,
      operationsSolutions: null,
    });
  }, []);

  return {
    downloadState,
    startDownload,
    validateRoutes,
    resetDownload,
  };
}

